import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel";
import { getPosts, getSettings } from "@/lib/api";
import { categoryStyle } from "@/lib/constants";

/**
 * 4 columns × 2 rows of 1:1 square images.
 * We chunk posts into pages of 8, and embla scrolls page-by-page.
 */
function chunk(arr, n) {
    const out = [];
    for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
    return out;
}

export default function MostRecentSlider() {
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        getSettings()
            .then((s) =>
                getPosts(
                    "recent",
                    s.countdown_enabled ? { withinHours: 72 } : {},
                ),
            )
            .then(setPosts)
            .catch(() => setPosts([]));
    }, []);

    const pages = chunk(posts, 8);

    return (
        <section
            className="px-4 md:px-8 lg:px-12 mt-16"
            data-testid="recent-section"
        >
            <h2 className="section-title mb-6" data-testid="recent-title">
                Most Recent
            </h2>
            {posts.length === 0 ? (
                <p
                    className="font-sofia text-[#1B1B1B]/60 italic"
                    data-testid="recent-empty"
                >
                    No recent posts in the last 72 hours. Visit{" "}
                    <a href="/categories" className="text-[#E63B26] underline">
                        Categories
                    </a>{" "}
                    for the full archive.
                </p>
            ) : (
            <div className="relative" data-testid="most-recent-slider">
                <Carousel
                    opts={{ align: "start", loop: false }}
                    className="w-full"
                    data-testid="recent-carousel"
                >
                    <CarouselContent className="-ml-4">
                        {pages.map((page, pi) => (
                            <CarouselItem
                                key={pi}
                                className="pl-4 basis-full"
                                data-testid={`recent-page-${pi}`}
                            >
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {page.map((post) => (
                                        <Link
                                            to={`/post/${post.slug}`}
                                            key={post.id}
                                            className="post-card relative block overflow-hidden bg-[#1B1B1B]"
                                            data-testid={`recent-card-link-${post.slug}`}
                                        >
                                            <div className="relative aspect-square overflow-hidden">
                                                <img
                                                    src={post.image_url}
                                                    alt={post.title}
                                                    className="post-card-image absolute inset-0 w-full h-full object-cover"
                                                    loading="lazy"
                                                />
                                                <span
                                                    className="badge-category absolute top-2 left-2 z-10 text-[0.6rem] px-2 py-1"
                                                    style={categoryStyle(post.category)}
                                                >
                                                    {post.category}
                                                </span>
                                                <div className="post-card-caption text-[0.7rem]">
                                                    {post.title.length > 40
                                                        ? post.title.slice(
                                                              0,
                                                              37,
                                                          ) + "..."
                                                        : post.title}
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                    {pages.length > 1 && (
                        <>
                            <CarouselPrevious
                                className="left-2 bg-white border-none h-12 w-12 rounded-none shadow"
                                data-testid="recent-prev"
                            />
                            <CarouselNext
                                className="right-2 bg-[#1B1B1B] text-white border-none h-12 w-12 rounded-none hover:bg-[#E63B26]"
                                data-testid="recent-next"
                            />
                        </>
                    )}
                </Carousel>
            </div>
            )}
        </section>
    );
}
